import React, { useState } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  GripVertical,
  Eye,
  EyeOff,
  Star,
  ExternalLink
} from 'lucide-react';
import { useCMS } from '../../store/cmsStore';
import type { Package } from '../../store/cmsStore';

export default function PackageEditor() {
  const { data, addPackage, updatePackage, deletePackage } = useCMS();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<Omit<Package, 'id'>>({
    name: '',
    badge: '',
    description: '',
    features: [],
    link: '',
    color: 'brown',
    popular: false,
    active: true,
    order: 0,
  });
  const [newFeature, setNewFeature] = useState('');

  const handleEdit = (pkg: Package) => {
    setEditingId(pkg.id);
    setFormData({
      name: pkg.name,
      badge: pkg.badge,
      description: pkg.description,
      features: [...pkg.features],
      link: pkg.link,
      color: pkg.color,
      popular: pkg.popular,
      active: pkg.active,
      order: pkg.order,
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      name: '',
      badge: '',
      description: '',
      features: [],
      link: '',
      color: 'brown',
      popular: false,
      active: true,
      order: data.packages.length + 1,
    });
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('يرجى إدخال اسم الباقة');
      return;
    }
    if (editingId) {
      updatePackage(editingId, formData);
    } else {
      addPackage(formData);
    }
    setEditingId(null);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
  };

  const handleDelete = (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذه الباقة؟')) {
      deletePackage(id);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const toggleActive = (id: string, currentActive: boolean) => {
    updatePackage(id, { active: !currentActive });
  };

  const togglePopular = (id: string, currentPopular: boolean) => {
    updatePackage(id, { popular: !currentPopular });
  };

  const sortedPackages = [...data.packages].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 font-amiri">إدارة الباقات</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-brown-600 text-white rounded-lg hover:bg-brown-700 transition font-cairo"
        >
          <Plus className="w-4 h-4" />
          إضافة باقة جديدة
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 font-cairo">
              {editingId ? 'تعديل الباقة' : 'إضافة باقة جديدة'}
            </h3>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                اسم الباقة *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                placeholder="مثال: الباقة الاقتصادية"
              />
            </div>

            {/* Badge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                الشارة
              </label>
              <input
                type="text"
                value={formData.badge}
                onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                placeholder="مثال: الأكثر طلباً"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                الوصف
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                rows={3}
                placeholder="وصف مختصر للباقة"
              />
            </div>

            {/* Features */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                المميزات
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                  placeholder="أضف ميزة جديدة"
                />
                <button
                  onClick={addFeature}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-cairo"
                >
                  إضافة
                </button>
              </div>
              <div className="space-y-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="flex-1 text-sm font-cairo">{feature}</span>
                    <button
                      onClick={() => removeFeature(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Link */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                رابط واتساب
              </label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                placeholder="https://wa.me/..."
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                اللون
              </label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
              >
                <option value="brown">بني</option>
                <option value="gold">ذهبي</option>
                <option value="navy">كحلي</option>
                <option value="green">أخضر</option>
                <option value="blue">أزرق</option>
              </select>
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                الترتيب
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                min="1"
              />
            </div>

            {/* Toggles */}
            <div className="md:col-span-2 flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.popular}
                  onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                  className="w-4 h-4 text-brown-600 rounded focus:ring-brown-500"
                />
                <span className="text-sm font-cairo text-gray-700">الأكثر شعبية</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-brown-600 rounded focus:ring-brown-500"
                />
                <span className="text-sm font-cairo text-gray-700">نشط</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-cairo transition"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-brown-600 text-white rounded-lg hover:bg-brown-700 font-cairo transition"
            >
              <Save className="w-4 h-4" />
              حفظ
            </button>
          </div>
        </div>
      )}

      {/* Packages List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الباقة</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الشارة</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">المميزات</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الحالة</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الشعبية</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedPackages.map((pkg) => (
                <tr key={pkg.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="font-bold text-gray-900 font-cairo">{pkg.name}</div>
                    <div className="text-sm text-gray-500 font-cairo mt-1 line-clamp-1">{pkg.description}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block bg-brown-100 text-brown-700 px-3 py-1 rounded-full text-xs font-cairo">
                      {pkg.badge}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-gray-600 font-cairo">{pkg.features.length} ميزة</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => toggleActive(pkg.id, pkg.active)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-cairo transition ${
                        pkg.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {pkg.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {pkg.active ? 'نشط' : 'مخفي'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => togglePopular(pkg.id, pkg.popular)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-cairo transition ${
                        pkg.popular
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      <Star className={`w-3 h-3 ${pkg.popular ? 'fill-current' : ''}`} />
                      {pkg.popular ? 'مميز' : 'عادي'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEdit(pkg)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="تعديل"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {pkg.link && (
                        <a
                          href={pkg.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                          title="اختبار الرابط"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(pkg.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.packages.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-cairo">لا توجد باقات بعد</p>
          </div>
        )}
      </div>
    </div>
  );
}